import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Crown } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { useSortable, SortableTh } from "@/components/SortableTh";

export const CustomersManager = ({ orders = [], format, search = "" }) => {
  const customers = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const id = o.user_id || "unknown";
      if (!map[id]) map[id] = { id, name: o.customer_name || "Customer", email: o.customer_email || "", orders: 0, spent: 0, last: o.created_at };
      map[id].orders += 1;
      map[id].spent += (o.seller_total ?? (o.items || []).reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0));
      if (o.created_at > map[id].last) map[id].last = o.created_at;
      if (o.customer_name) map[id].name = o.customer_name;
      if (o.customer_email) map[id].email = o.customer_email;
    });
    return Object.values(map).sort((a, b) => b.spent - a.spent);
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [customers, search]);

  const totalRevenue = customers.reduce((s, c) => s + c.spent, 0);
  const { sorted, sortKey, dir, toggle } = useSortable(filtered, {
    name: (c) => c.name || "",
    last: (c) => c.last,
    orders: (c) => c.orders,
    spent: (c) => c.spent,
  });
  const kpis = [
    { label: "Customers", value: customers.length },
    { label: "Total revenue", value: format(totalRevenue) },
    { label: "Avg. per customer", value: format(customers.length ? totalRevenue / customers.length : 0) },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4" data-testid={`cust-kpi-${k.label}`}>
            <p className="text-xl sm:text-2xl font-black font-display">{k.value}</p>
            <p className="text-xs text-muted-foreground">{k.label}</p>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-end">
        <ExportMenu filename="customers" title="Customers" testid="customers-export"
          columns={[
            { header: "Customer", accessor: (c) => c.name },
            { header: "Email", accessor: (c) => c.email },
            { header: "Orders", accessor: (c) => c.orders },
            { header: "Spent (EUR)", accessor: (c) => c.spent.toFixed(2) },
            { header: "Last order", accessor: (c) => new Date(c.last).toLocaleDateString() },
          ]}
          rows={filtered} />
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTh label="Customer" sortKey="name" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="Last order" sortKey="last" activeKey={sortKey} dir={dir} onSort={toggle} className="hidden sm:table-cell" />
              <SortableTh label="Orders" sortKey="orders" activeKey={sortKey} dir={dir} onSort={toggle} className="text-center" />
              <SortableTh label="Spent" sortKey="spent" activeKey={sortKey} dir={dir} onSort={toggle} className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c, idx) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-sm shrink-0">
                      {(c.name || "C").trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate max-w-[200px] flex items-center gap-1.5">
                        {c.name}{idx === 0 && customers.length > 1 && <Crown className="w-3.5 h-3.5 text-gold" title="Top customer" />}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[200px]">{c.email || `#${c.id.slice(-6)}`}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{new Date(c.last).toLocaleDateString()}</TableCell>
                <TableCell className="text-center"><Badge variant="outline">{c.orders}</Badge></TableCell>
                <TableCell className="text-right text-sm font-semibold">{format(c.spent)}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {customers.length === 0 ? "No customers yet. They appear after their first paid order." : "No customers match your search."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
